import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import {
  createPurchaseOrder,
  getLowStockForPo,
  listPurchaseOrders,
  type PoStatus,
} from '@/lib/api/purchase-orders.js';
import { listInventoryItems, type InventoryItemListRow } from '@/lib/api/inventory.js';
import { SupplierSelect } from '@/components/suppliers/SupplierSelect.js';
import { SlideOver } from '@/components/SlideOver.js';
import { Pagination } from '@/components/common/Pagination.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function statusBadge(status: PoStatus): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'sent':
      return 'bg-blue-100 text-blue-800';
    case 'partially_received':
      return 'bg-amber-100 text-amber-900';
    case 'received':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export function PurchaseOrderListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const listQuery = useQuery({
    queryKey: ['purchase-orders', page],
    queryFn: () => listPurchaseOrders({ page, pageSize }),
  });

  const meta = listQuery.data?.meta;

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477]"
        >
          <Plus className="h-4 w-4" />
          New PO
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">PO #</th>
              <th className="px-5 py-3">Supplier</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Items</th>
              <th className="px-5 py-3">Total</th>
              <th className="px-5 py-3">Expected</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {listQuery.data?.data.map((po) => (
              <tr key={po.id} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-mono font-semibold text-slate-950">{po.poNumber}</td>
                <td className="px-5 py-4 text-slate-700">{po.supplierName ?? '—'}</td>
                <td className="px-5 py-4">
                  <span className={['rounded-full px-2 py-0.5 text-xs font-bold', statusBadge(po.status)].join(' ')}>
                    {prettyLabel(po.status)}
                  </span>
                </td>
                <td className="px-5 py-4">{po.lineCount ?? 0} lines</td>
                <td className="px-5 py-4">{formatCurrency(po.total)}</td>
                <td className="px-5 py-4 text-slate-600">{formatDate(po.expectedAt)}</td>
                <td className="px-5 py-4 text-right">
                  <button
                    type="button"
                    className="text-sm font-semibold text-primary"
                    onClick={() => navigate(`/inventory/purchase-orders/${po.id}`)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
            {listQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : null}
            {!listQuery.isLoading && listQuery.data?.data.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                  No purchase orders
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={meta?.pageSize ?? pageSize}
        total={meta?.total ?? 0}
        onPageChange={setPage}
      />

      <SlideOver title="New purchase order" open={createOpen} onClose={() => setCreateOpen(false)}>
        <NewPoForm
          onCreated={(id) => {
            queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
            setCreateOpen(false);
            navigate(`/inventory/purchase-orders/${id}`);
          }}
        />
      </SlideOver>
    </div>
  );
}

function NewPoForm({ onCreated }: { onCreated: (id: string) => void }) {
  const lowStockQuery = useQuery({ queryKey: ['po-low-stock'], queryFn: getLowStockForPo });

  const [supplierId, setSupplierId] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [shippingCost, setShippingCost] = useState('0');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [lines, setLines] = useState<Array<{ itemId: string; name: string; qty: string; unitCost: string }>>([]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const searchQuery = useQuery({
    queryKey: ['po-item-search', debounced],
    queryFn: () => listInventoryItems({ search: debounced, page: 1, pageSize: 15, sellableOnly: true }),
    enabled: debounced.trim().length >= 2,
  });

  const createMut = useMutation({
    mutationFn: () =>
      createPurchaseOrder({
        supplierId,
        expectedAt: expectedAt ? new Date(expectedAt).toISOString() : undefined,
        shippingCost: Number(shippingCost),
        notes: notes || undefined,
        lines: lines.map((l) => ({
          itemId: l.itemId,
          qtyOrdered: Number(l.qty),
          unitCost: Number(l.unitCost),
        })),
      }),
    onSuccess: (data) => {
      toastSuccess('Purchase order created');
      onCreated(data.id);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function addLineFromItem(item: InventoryItemListRow, qty = 1, cost = Number(item.retailPrice)) {
    if (lines.some((l) => l.itemId === item.id)) return;
    setLines((prev) => [...prev, { itemId: item.id, name: item.name, qty: String(qty), unitCost: String(cost) }]);
  }

  function addLowStockLines() {
    if (!supplierId) {
      toastError('Select a supplier first');
      return;
    }
    const groups = lowStockQuery.data ?? [];
    const group = groups.find((g) => g.supplierId === supplierId);
    const items = group?.items ?? [];
    for (const it of items) {
      addLineFromItem(
        {
          id: it.itemId,
          name: it.itemName,
          category: 'repair_part',
          qtyOnHand: String(it.qtyOnHand),
          retailPrice: '0',
          lowStock: true,
        },
        Math.max(it.reorderQty, 1),
        0,
      );
    }
    if (!items.length) toastError('No low-stock items for this supplier');
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!supplierId || lines.length === 0) {
      toastError('Supplier and at least one line required');
      return;
    }
    createMut.mutate();
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Supplier</span>
        <div className="mt-1">
          <SupplierSelect value={supplierId} onChange={setSupplierId} required />
        </div>
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Expected date</span>
        <input
          type="datetime-local"
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={expectedAt}
          onChange={(e) => setExpectedAt(e.target.value)}
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Shipping cost</span>
        <input
          type="number"
          min="0"
          step="0.01"
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={shippingCost}
          onChange={(e) => setShippingCost(e.target.value)}
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Notes</span>
        <textarea className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>

      <div className="border-t border-slate-200 pt-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-slate-800">Line items</span>
          <button type="button" className="text-xs font-semibold text-primary" onClick={addLowStockLines}>
            Add from low stock
          </button>
        </div>
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-3 text-sm"
            placeholder="Search catalog…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {searchQuery.data?.data.length ? (
          <ul className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white">
            {searchQuery.data.data.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onClick={() => addLineFromItem(item)}
                >
                  <span>{item.name}</span>
                  <span className="text-xs text-slate-500">Add</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <ul className="mt-3 space-y-2">
          {lines.map((l) => (
            <li key={l.itemId} className="rounded-xl border border-slate-200 p-3 text-sm">
              <div className="font-semibold text-slate-900">{l.name}</div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label>
                  Qty
                  <input
                    className="mt-1 w-full rounded border px-2 py-1"
                    value={l.qty}
                    onChange={(e) =>
                      setLines((prev) => prev.map((x) => (x.itemId === l.itemId ? { ...x, qty: e.target.value } : x)))
                    }
                  />
                </label>
                <label>
                  Unit cost
                  <input
                    className="mt-1 w-full rounded border px-2 py-1"
                    value={l.unitCost}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((x) => (x.itemId === l.itemId ? { ...x, unitCost: e.target.value } : x)),
                      )
                    }
                  />
                </label>
              </div>
              <button
                type="button"
                className="mt-2 text-xs font-semibold text-red-700"
                onClick={() => setLines((prev) => prev.filter((x) => x.itemId !== l.itemId))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>

      <button
        type="submit"
        disabled={createMut.isPending}
        className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white disabled:opacity-60"
      >
        {createMut.isPending ? 'Saving…' : 'Save as draft'}
      </button>
    </form>
  );
}
